import { InfoOutlineIcon } from '@chakra-ui/icons';
import { Box, Center, HStack, Spinner, VStack } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import Button from 'components/Button';
import Icon from 'components/Icon';
import Text from 'components/Text';
import { ERoute, TanstackQueryName } from 'types/frontend';

import CVPreview from './components/CVPreview';
import { useCVStream } from './hooks/useCVStream';
import { CVData, CVPersonalInfo, CVSection, CVSettings, CVTemplate } from './types';
import { createDefaultPersonalInfo, createDefaultSettings } from './utils/cvDefaults';

/**
 * Location state for "Import from PDF" mode.
 * Passed from PDFUploadTab when navigating to this view.
 */
interface ImportLocationState {
  mode: 'import';
  pdfBase64: string;
  template: CVTemplate;
}

/**
 * Location state for "Create with AI" mode.
 * Passed from NewCV when navigating to this view.
 */
interface GenerateLocationState {
  mode: 'generate';
  role: string;
  template: CVTemplate;
  fillFromBDS: boolean;
}

type StreamingLocationState = ImportLocationState | GenerateLocationState;

interface ProgressStep {
  id: string;
  label: string;
  status: 'pending' | 'active' | 'complete';
}

const IMPORT_STEPS: ProgressStep[] = [
  { id: 'extracting', label: 'Analyzing your CV...', status: 'pending' },
  { id: 'extracting_personalInfo', label: 'Extracting personal info...', status: 'pending' },
  { id: 'extracting_experience', label: 'Extracting work experience...', status: 'pending' },
  { id: 'extracting_education', label: 'Extracting education...', status: 'pending' },
  { id: 'extracting_skills', label: 'Extracting skills & more...', status: 'pending' },
  { id: 'saving', label: 'Saving your CV...', status: 'pending' },
];

const GENERATE_STEPS: ProgressStep[] = [
  { id: 'generating', label: 'Preparing your CV...', status: 'pending' },
  { id: 'generating_personalInfo', label: 'Creating personal info...', status: 'pending' },
  { id: 'generating_experience', label: 'Creating work experience...', status: 'pending' },
  { id: 'generating_education', label: 'Creating education...', status: 'pending' },
  { id: 'generating_skills', label: 'Creating skills & more...', status: 'pending' },
  { id: 'saving', label: 'Saving your CV...', status: 'pending' },
];

/** Minimum ms between step transitions to create a sequential animation. */
const MIN_STEP_DELAY = 600;

/**
 * Given the current stage from SSE events, update steps to reflect progress.
 * All steps up to (but not including) the matching stage are complete.
 * The matching stage is active. Later stages remain pending.
 */
export const deriveSteps = (steps: ProgressStep[], stage: string): ProgressStep[] => {
  // Find the best matching step
  let activeIndex = steps.findIndex((s) => s.id === stage);

  // Some stages (like extracting_projects, generating_projects) don't have
  // dedicated steps — bucket them under the "skills & more" catch-all
  if (activeIndex === -1) {
    if (stage.startsWith('extracting_')) {
      activeIndex = steps.findIndex((s) => s.id === 'extracting_skills');
    } else if (stage.startsWith('generating_')) {
      activeIndex = steps.findIndex((s) => s.id === 'generating_skills');
    }
  }

  if (activeIndex === -1) return steps;

  return steps.map((step, i) => {
    if (i < activeIndex) return { ...step, status: 'complete' };
    if (i === activeIndex) return { ...step, status: 'active' };

    return { ...step, status: 'pending' };
  });
};

const isValidState = (state: unknown): state is StreamingLocationState => {
  if (!state || typeof state !== 'object') return false;
  const s = state as Record<string, unknown>;

  if (s.mode === 'import') return typeof s.pdfBase64 === 'string' && !!s.pdfBase64;
  if (s.mode === 'generate') return typeof s.role === 'string' && !!s.role;

  return false;
};

const CVStreaming = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { toastError } = useToastWrapper();
  const { id: currentUserId } = useCurrentUser();
  const hasStartedRef = useRef(false);

  const state = location.state as StreamingLocationState | null;
  const isValid = isValidState(state);
  const isImport = state?.mode === 'import';

  const initialSteps = isImport ? IMPORT_STEPS : GENERATE_STEPS;
  const [steps, setSteps] = useState<ProgressStep[]>(initialSteps);
  const [error, setError] = useState<string | null>(null);

  // Build CV data progressively as sections arrive
  const [personalInfo, setPersonalInfo] = useState<CVPersonalInfo>(createDefaultPersonalInfo);
  const [sections, setSections] = useState<CVSection[]>([]);
  const [settings] = useState<CVSettings>(createDefaultSettings);
  const template: CVTemplate = state?.template || 'classic';

  const previewData: CVData = { personalInfo, sections };

  const handleSection = useCallback((sectionType: string, data: unknown) => {
    if (sectionType === 'personalInfo') {
      setPersonalInfo((prev) => ({ ...prev, ...(data as Partial<CVPersonalInfo>) }));
    } else if (sectionType === 'title') {
      // Title is handled as a meta field, not a section in the preview
    } else {
      // CV section — data shape: { id, type, title, entries }
      const sectionData = data as CVSection;

      setSections((prev) => [...prev, sectionData]);
    }
  }, []);

  // Queue individual step indices so each step gets its own active → complete transition.
  // When a stage arrives that would skip steps (e.g. jumping from step 0 to step 3),
  // we enqueue each intermediate step to animate them one-by-one.
  const stepQueueRef = useRef<number[]>([]);
  const isProcessingRef = useRef(false);
  const lastEnqueuedIndexRef = useRef(-1);
  const hasInvalidatedTokenRef = useRef(false);

  const processStepQueue = useCallback(() => {
    if (isProcessingRef.current || stepQueueRef.current.length === 0) return;

    isProcessingRef.current = true;
    const targetIndex = stepQueueRef.current.shift()!;

    setSteps((prev) =>
      prev.map((step, i) => {
        if (i < targetIndex) return { ...step, status: 'complete' };
        if (i === targetIndex) return { ...step, status: 'active' };

        return { ...step, status: 'pending' };
      })
    );

    setTimeout(() => {
      isProcessingRef.current = false;
      processStepQueue();
    }, MIN_STEP_DELAY);
  }, []);

  const handleProgress = useCallback(
    (stage: string) => {
      // Token is consumed server-side before first SSE event — refresh topbar balance once
      if (!hasInvalidatedTokenRef.current) {
        hasInvalidatedTokenRef.current = true;
        queryClient.invalidateQueries({
          queryKey: [TanstackQueryName.GetCurrentUser, currentUserId],
        });
      }

      // Resolve stage → step index (same logic as deriveSteps)
      let targetIndex = initialSteps.findIndex((s) => s.id === stage);

      if (targetIndex === -1) {
        if (stage.startsWith('extracting_')) {
          targetIndex = initialSteps.findIndex((s) => s.id === 'extracting_skills');
        } else if (stage.startsWith('generating_')) {
          targetIndex = initialSteps.findIndex((s) => s.id === 'generating_skills');
        }
      }

      if (targetIndex === -1 || targetIndex <= lastEnqueuedIndexRef.current) return;

      // Enqueue each step from (lastEnqueued + 1) up to targetIndex
      for (let i = lastEnqueuedIndexRef.current + 1; i <= targetIndex; i++) {
        stepQueueRef.current.push(i);
      }

      lastEnqueuedIndexRef.current = targetIndex;
      processStepQueue();
    },
    [initialSteps, processStepQueue, queryClient, currentUserId]
  );

  const handleDone = useCallback(
    (payload?: Record<string, unknown>) => {
      const cvId = payload?.cvId as string | undefined;
      if (!cvId) return;

      const wasTruncated = payload?.truncated === true;

      // Enqueue any remaining steps that haven't been reached yet
      const lastStep = initialSteps.length - 1;

      for (let i = lastEnqueuedIndexRef.current + 1; i <= lastStep; i++) {
        stepQueueRef.current.push(i);
      }

      lastEnqueuedIndexRef.current = lastStep;

      // Wait for queue to drain, then mark all complete and navigate
      const waitAndFinalize = () => {
        if (isProcessingRef.current || stepQueueRef.current.length > 0) {
          // Still processing — let processStepQueue handle it, check again soon
          if (!isProcessingRef.current) processStepQueue();
          setTimeout(waitAndFinalize, 200);

          return;
        }

        // All steps processed — mark all complete
        setSteps((prev) => prev.map((s) => ({ ...s, status: 'complete' as const })));

        queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetCVs, currentUserId] });
        queryClient.invalidateQueries({
          queryKey: [TanstackQueryName.GetCurrentUser, currentUserId],
        });

        setTimeout(() => {
          navigate(`${ERoute.CVBuilder}/${cvId}`, {
            replace: true,
            state: wasTruncated ? { truncated: true } : undefined,
          });
        }, 800);
      };

      waitAndFinalize();
    },
    [initialSteps, navigate, queryClient, currentUserId, processStepQueue]
  );

  const handleError = useCallback(
    (message: string) => {
      setError(message);
      toastError(message || 'Something went wrong.');
    },
    [toastError]
  );

  const { sendRequest, cancelStream } = useCVStream({
    onSection: handleSection,
    onProgress: handleProgress,
    onDone: handleDone,
    onError: handleError,
  });

  // Redirect if no valid location state (e.g. page refresh)
  useEffect(() => {
    if (!isValid) {
      navigate(`${ERoute.CVBuilder}${ERoute.NewCV}`, { replace: true });
    }
  }, [isValid, navigate]);

  // Start streaming when component mounts (once)
  useEffect(() => {
    if (!isValid || hasStartedRef.current) return;
    hasStartedRef.current = true;

    if (state.mode === 'import') {
      sendRequest('import-cv', {
        pdfBase64: state.pdfBase64,
        template: state.template,
      });
    } else {
      sendRequest('create-cv', {
        role: state.role,
        template: state.template,
        fillFromBDS: state.fillFromBDS,
      });
    }

    return () => {
      cancelStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If no valid state, we redirect above — don't render anything
  if (!isValid) return null;

  const handleRetry = () => {
    setError(null);
    setSteps(initialSteps);
    setPersonalInfo(createDefaultPersonalInfo());
    setSections([]);
    hasStartedRef.current = false;
    stepQueueRef.current = [];
    isProcessingRef.current = false;
    lastEnqueuedIndexRef.current = -1;
    hasInvalidatedTokenRef.current = false;

    if (state.mode === 'import') {
      sendRequest('import-cv', {
        pdfBase64: state.pdfBase64,
        template: state.template,
      });
    } else {
      sendRequest('create-cv', {
        role: state.role,
        template: state.template,
        fillFromBDS: state.fillFromBDS,
      });
    }
  };

  return (
    <HStack width="100%" height="100%" spacing={0} alignItems="stretch">
      {/* Left: Progress Stepper */}
      <Box
        width="320px"
        minWidth="320px"
        borderRight="1px solid"
        borderColor="whiteAlpha.100"
        p={8}
        display="flex"
        flexDirection="column"
      >
        <VStack spacing={2} alignItems="flex-start" mb={8}>
          <Text fontSize="xLarge" fontWeight="bold">
            {isImport ? 'Importing CV' : 'Generating CV'}
          </Text>
          <Text fontSize="small" opacity="secondary">
            {isImport
              ? 'Extracting data from your PDF...'
              : 'Creating a professional CV for you...'}
          </Text>
        </VStack>

        <VStack spacing={0} alignItems="stretch" flex={1}>
          {steps.map((step, i) => (
            <HStack key={step.id} spacing={3} py={3}>
              {/* Step indicator */}
              <Center
                width="28px"
                height="28px"
                borderRadius="full"
                flexShrink={0}
                backgroundColor={
                  step.status === 'complete'
                    ? 'green.500'
                    : step.status === 'active'
                      ? 'purple.500'
                      : 'whiteAlpha.200'
                }
                transition="all 0.3s"
              >
                {step.status === 'complete' ? (
                  <Icon type="CheckMark" size={14} />
                ) : step.status === 'active' ? (
                  <Spinner size="xs" color="white" />
                ) : (
                  <Text fontSize="xMicro" opacity="tertiary">
                    {i + 1}
                  </Text>
                )}
              </Center>

              {/* Step label */}
              <Text
                fontSize="small"
                fontWeight={step.status === 'active' ? 'semibold' : undefined}
                opacity={step.status === 'pending' ? 'tertiary' : 'primary'}
                transition="all 0.3s"
              >
                {step.status === 'complete' && step.id === 'saving'
                  ? 'Done!'
                  : step.label}
              </Text>
            </HStack>
          ))}
        </VStack>

        {/* Error state */}
        {error && (
          <VStack spacing={3} pt={4}>
            <Box
              p={3}
              borderRadius="md"
              backgroundColor="red.900"
              border="1px solid"
              borderColor="red.600"
              width="100%"
            >
              <Text fontSize="small" color="red.200">
                {error}
              </Text>
            </Box>
            <Button scheme="outline" size="sm" onClick={handleRetry} width="100%">
              Retry
            </Button>
          </VStack>
        )}

        {/* Info box — don't leave warning */}
        {!error && (
          <HStack
            spacing={2}
            mt={4}
            p={3}
            borderRadius="md"
            backgroundColor="whiteAlpha.50"
            border="1px solid"
            borderColor="whiteAlpha.100"
          >
            <Box color="purple.300" flexShrink={0} display="flex" alignItems="center">
              <InfoOutlineIcon boxSize={3.5} />
            </Box>
            <Text fontSize="micro" opacity="secondary">
              Please stay on this page until complete. Leaving may result in lost data.
            </Text>
          </HStack>
        )}
      </Box>

      {/* Right: CV Preview */}
      <Box flex={1} overflow="hidden">
        <CVPreview data={previewData} settings={settings} template={template} />
      </Box>
    </HStack>
  );
};

export default CVStreaming;
